/* eslint-disable no-undef */
import React, { useState, useEffect, useRef } from "react";
import {
  Grid,
  Button,
  Box,
  Link,
  TextField,
  CircularProgress,
} from "@mui/material";
import { FormattedMessage } from "react-intl";
import { ArrowRight } from "lucide-react";
import PhoneInput from "react-phone-input-2";
import sh from "bundles/common/utils/sh";
import validator from "validator";
import { useSnackbar } from "notistack";
import {
  getResponseMessageId,
  countryMap,
} from "bundles/common/utils/constants";
import PasswordField from "bundles/common/components/PasswordField";
import TurnstileWidget from "bundles/common/components/TurnstileWidget";
import { useSelector, useDispatch } from "react-redux";
import OauthButton from "bundles/common/components/OauthButton";
import TextDivider from "bundles/common/components/TextDivider";
import { useLocation } from "react-router-dom";
import {
  trackCandidateSignup,
  activeTrackingScripts,
} from "bundles/common/utils/marketing";
import { replaceCurrentCandidate } from "../../constants/candidateConstants";
import { getStoredFeatureSessionId } from "bundles/common/utils/trackEvent";
import { track } from "bundles/common/utils/tracking";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "0.5rem",
    fontFamily: "Segoe UI Regular, system-ui, sans-serif",
    fontSize: "0.875rem",
    "& fieldset": { borderColor: "hsl(220, 13%, 91%)" },
    "&:hover fieldset": { borderColor: "hsl(220, 13%, 80%)" },
    "&.Mui-focused fieldset": {
      border: "2px solid hsl(153 68% 25%)",
    },
  },
  "& .MuiOutlinedInput-input": {
    color: "hsl(220, 25%, 12%)",
  },
  "& .MuiInputLabel-root": {
    color: "hsl(220, 10%, 46%)",
    fontFamily: "Segoe UI Regular, system-ui, sans-serif",
    fontSize: "0.875rem",
    "&.Mui-focused": {
      color: "hsl(153 68% 25%)",
    },
  },
};

const SignUp = ({ onSectionClicked, onActionFinished, customScope }) => {
  const { enqueueSnackbar } = useSnackbar();
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const authenticity_token = useSelector((state) => state.authenticity_token);
  const railsEnv = useSelector((state) => state.railsEnv);

  const initialFormValues = {
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    password: "",
    utm_source: "",
    utm_medium: "",
    utm_campaign: "",
  };

  const dispatch = useDispatch();
  const location = useLocation();
  const oauthError = new URLSearchParams(location.search).get("oauth_error");

  const [values, setValues] = useState(initialFormValues);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [signUpWay, setSignUpWay] = useState("oauth");
  const [url, setUrl] = useState("");
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);

  // Reload bfcache restores so the server can redirect signed-in candidates.
  useEffect(() => {
    const onPageShow = (e) => {
      if (e.persisted) window.location.reload();
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setValues((prevValues) => ({
      ...prevValues,
      utm_source: params.get("utm_source") || "",
      utm_medium: params.get("utm_medium") || "",
      utm_campaign: params.get("utm_campaign") || "",
    }));
  }, [location.search]);

  const validate = (fieldValues = values) => {
    let temp = { ...errors };
    let required = (
      <FormattedMessage id="form_validation.required"></FormattedMessage>
    );
    let not_an_email = (
      <FormattedMessage id="form_validation.not_an_email"></FormattedMessage>
    );
    let not_a_strong_password = (
      <FormattedMessage id="form_validation.not_a_strong_password"></FormattedMessage>
    );

    if ("firstName" in fieldValues) {
      temp.firstName = fieldValues.firstName ? "" : required;
    }

    if ("lastName" in fieldValues) {
      temp.lastName = fieldValues.lastName ? "" : required;
    }

    if ("phone" in fieldValues) {
      temp.phone = fieldValues.phone ? "" : required;
    }

    if ("email" in fieldValues) {
      temp.email = fieldValues.email ? "" : required;
      if (fieldValues.email && !validator.isEmail(fieldValues.email))
        temp.email = not_an_email;
    }

    if ("password" in fieldValues) {
      temp.password = fieldValues.password ? "" : required;
      if (
        fieldValues.password &&
        !validator.isStrongPassword(fieldValues.password)
      ) {
        temp.password = not_a_strong_password;
      }
    }

    setErrors({
      ...temp,
    });

    return Object.values(temp).every((x) => x === "");
  };

  const handleInputValue = (e) => {
    const { name, value } = e.target;
    setValues({
      ...values,
      [name]: value,
    });
    validate({ [name]: value });
  };

  const handleSubmit = (e) => {
    setLoading(true);
    e.preventDefault();
    if (validate(values)) {
      sh.post(`/${i18nLocale}/candidates`, {
        candidate: {
          first_name: values.firstName,
          last_name: values.lastName,
          phone: values.phone,
          email: values.email,
          password: values.password,
          utm_source: values.utm_source,
          utm_medium: values.utm_medium,
          utm_campaign: values.utm_campaign,
          active_tracking_scripts: activeTrackingScripts(),
        },
        turnstile_token: turnstileToken,
        url,
        feature_session_id: getStoredFeatureSessionId(),
      })
        .then(() => {
          if (railsEnv === "production") trackCandidateSignup();
          track("sign_up", { user_type: "candidate", method: "email" }, () => {
            replaceCurrentCandidate(dispatch, authenticity_token, () => {
              // replace: keep the sign-up form out of history.
              window.location.replace(`${window.location.origin}/${i18nLocale}/candidate?showOnboarding=true&justSignedUp=true`);
            });
          });
        })
        .catch((err) => {
          if (
            err.response?.status === 403 &&
            err.response?.data?.error === "captcha_required"
          ) {
            setCaptchaRequired(true);
            setTurnstileToken(null);
            turnstileRef.current?.reset();
            setLoading(false);
            return;
          }
          if (err.response?.data?.message) {
            enqueueSnackbar(
              getResponseMessageId(err?.response?.data?.message),
              { variant: "error" }
            );
          }
          console.error("Error", err);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  };

  const primaryBtnSx = {
    bgcolor: "hsl(153 68% 25%)",
    color: "white",
    fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
    fontWeight: 700,
    fontSize: "0.875rem",
    px: "1.5rem",
    py: "0.75rem",
    borderRadius: "0.75rem",
    textTransform: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
    "&:hover": {
      bgcolor: "hsl(153 68% 25% / 0.9)",
    },
    "&.Mui-disabled": {
      bgcolor: "hsl(153 68% 25% / 0.3)",
      color: "hsl(0 0% 100% / 0.6)",
    },
  };

  return (
    <Box>
      <Box>
        {signUpWay == "oauth" ? (
          <Box sx={{ mx: "8px" }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sx={{ mt: "16px" }}>
                <Grid item xs={12} sx={{ mb: "16px" }}>
                  <OauthButton
                    scope={`${customScope}signup`}
                    provider="linkedin"
                  />
                  {oauthError && oauthError == "linkedin_error" && (
                    <Box
                      component="p"
                      sx={{
                        m: 0,
                        mt: "4px",
                        fontSize: "0.75rem",
                        color: "hsl(0, 70%, 50%)",
                        fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                      }}
                    >
                      <FormattedMessage id={"oauth.errors." + oauthError} />
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12} sx={{ mb: "16px" }}>
                  <OauthButton
                    scope={`${customScope}signup`}
                    provider="facebook"
                  />
                  {oauthError && oauthError == "facebook_error" && (
                    <Box
                      component="p"
                      sx={{
                        m: 0,
                        mt: "4px",
                        fontSize: "0.75rem",
                        color: "hsl(0, 70%, 50%)",
                        fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                      }}
                    >
                      <FormattedMessage id={"oauth.errors." + oauthError} />
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12} sx={{ mb: "16px" }}>
                  <OauthButton
                    scope={`${customScope}signup`}
                    provider="microsoft"
                  />
                  {oauthError && oauthError == "microsoft_error" && (
                    <Box
                      component="p"
                      sx={{
                        m: 0,
                        mt: "4px",
                        fontSize: "0.75rem",
                        color: "hsl(0, 70%, 50%)",
                        fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                      }}
                    >
                      <FormattedMessage id={"oauth.errors." + oauthError} />
                    </Box>
                  )}
                </Grid>

                <Grid item xs={12} sx={{ pb: "0" }} mt={2}>
                  <TextDivider labelId="oauth.divider" />
                </Grid>
              </Grid>

              <Grid item xs={12} sx={{ pb: "0" }}>
                <Button
                  fullWidth
                  onClick={() => setSignUpWay("old")}
                  sx={primaryBtnSx}
                >
                  <FormattedMessage id="form_sign_up" />
                  <ArrowRight size={14} />
                </Button>
              </Grid>
            </Grid>
          </Box>
        ) : (
          <Box>
            <Box sx={{ mb: "8px", textAlign: "center" }}>
              <Box
                component="span"
                sx={{
                  fontSize: "0.75rem",
                  fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                }}
              >
                <Link
                  onClick={() => setSignUpWay("oauth")}
                  underline="always"
                  sx={{
                    cursor: "pointer",
                    color: "hsl(153 68% 25%)",
                    textDecorationColor: "hsl(153 68% 25% / 0.4)",
                    "&:hover": {
                      textDecorationColor: "hsl(153 68% 25%)",
                    },
                  }}
                >
                  <FormattedMessage id="go_back_oauth" />
                </Link>
              </Box>
            </Box>
            <form onSubmit={handleSubmit} style={{ marginTop: "32px" }}>
              <Grid container justifyContent="center">
                <Grid item xs={12}>
                  <Box
                    sx={{ columnGap: "8px", display: "flex", width: "100%" }}
                  >
                    <Box sx={{ width: "100%" }}>
                      <TextField
                        fullWidth
                        required
                        type="text"
                        name="firstName"
                        size="small"
                        onBlur={handleInputValue}
                        onChange={handleInputValue}
                        variant="outlined"
                        sx={inputSx}
                        label={
                          <FormattedMessage id="devise.signup.first_name" />
                        }
                        {...(errors["firstName"] && {
                          error: true,
                          helperText: errors["firstName"],
                        })}
                      />
                    </Box>
                    <Box sx={{ width: "100%" }}>
                      <TextField
                        fullWidth
                        required
                        type="text"
                        size="small"
                        variant="outlined"
                        name="lastName"
                        onBlur={handleInputValue}
                        onChange={handleInputValue}
                        sx={inputSx}
                        {...(errors["lastName"] && {
                          error: true,
                          helperText: errors["lastName"],
                        })}
                        label={
                          <FormattedMessage id="devise.signup.last_name" />
                        }
                      />
                    </Box>
                  </Box>
                  <PhoneInput
                    country={"be"}
                    onlyCountries={countryMap.values}
                    containerStyle={{ marginTop: "8px", marginBottom: "8px" }}
                    inputStyle={{
                      height: "40px",
                      width: "100%",
                      borderRadius: "0.5rem",
                      border: "1px solid hsl(220, 13%, 91%)",
                      fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                      fontSize: "0.875rem",
                    }}
                    buttonStyle={{
                      borderRadius: "0.5rem 0 0 0.5rem",
                      border: "1px solid hsl(220, 13%, 91%)",
                    }}
                    value={values.phone}
                    inputProps={{
                      name: "phone",
                      required: true,
                      autoFocus: false,
                      onBlur: handleInputValue,
                      onChange: handleInputValue,
                    }}
                  />
                  <TextField
                    fullWidth
                    required
                    size="small"
                    type="email"
                    variant="outlined"
                    onBlur={handleInputValue}
                    onChange={handleInputValue}
                    name="email"
                    sx={inputSx}
                    {...(errors["email"] && {
                      error: true,
                      helperText: errors["email"],
                    })}
                    label={<FormattedMessage id="devise.signup.email" />}
                  />
                  <PasswordField
                    name="password"
                    labelId="devise.signup.password"
                    handleInputValue={handleInputValue}
                    errors={errors}
                  />
                  <Box
                    component="input"
                    type="text"
                    name="url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    sx={{
                      position: "absolute",
                      left: "-9999px",
                      width: "1px",
                      height: "1px",
                      opacity: 0,
                      pointerEvents: "none",
                    }}
                  />

                  {captchaRequired && (
                    <TurnstileWidget
                      ref={turnstileRef}
                      onToken={setTurnstileToken}
                      locale={i18nLocale}
                    />
                  )}

                  <Button
                    fullWidth
                    id="step-two-signup-button"
                    type="submit"
                    size="medium"
                    disabled={loading || (captchaRequired && !turnstileToken)}
                    sx={{
                      ...primaryBtnSx,
                      mt: "16px",
                    }}
                  >
                    <Box justifyContent="center">
                      {loading ? (
                        <CircularProgress size={16} sx={{ color: "white" }}>
                          <FormattedMessage id="devise.signup.build_career" />
                        </CircularProgress>
                      ) : (
                        <FormattedMessage id="devise.signup.build_career" />
                      )}
                    </Box>
                    {!loading && <ArrowRight size={14} />}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Box>
        )}
        <Grid container spacing={2} sx={{ mt: "8px" }}>
          <Grid item xs={12}>
            <Box
              component="span"
              sx={{
                fontSize: "0.6875rem",
                fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                color: "hsl(220, 10%, 46%)",
              }}
            >
              <FormattedMessage
                id="devise.signup.c_v_candidate"
                values={{
                  link1: (chunks) => (
                    <Link
                      href={`/${i18nLocale}/terms-of-use`}
                      sx={{
                        cursor: "pointer",
                        color: "hsl(153 68% 25%)",
                        textDecoration: "underline",
                        textDecorationColor: "hsl(153 68% 25% / 0.4)",
                        "&:hover": {
                          textDecorationColor: "hsl(153 68% 25%)",
                        },
                      }}
                    >
                      {chunks}
                    </Link>
                  ),
                  link2: (chunks) => (
                    <Link
                      href={`/${i18nLocale}/privacy-policy`}
                      sx={{
                        cursor: "pointer",
                        color: "hsl(153 68% 25%)",
                        textDecoration: "underline",
                        textDecorationColor: "hsl(153 68% 25% / 0.4)",
                        "&:hover": {
                          textDecorationColor: "hsl(153 68% 25%)",
                        },
                      }}
                    >
                      {chunks}
                    </Link>
                  ),
                  link3: (chunks) => (
                    <Link
                      href={`/${i18nLocale}/cookies`}
                      sx={{
                        cursor: "pointer",
                        color: "hsl(153 68% 25%)",
                        textDecoration: "underline",
                        textDecorationColor: "hsl(153 68% 25% / 0.4)",
                        "&:hover": {
                          textDecorationColor: "hsl(153 68% 25%)",
                        },
                      }}
                    >
                      {chunks}
                    </Link>
                  ),
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ textAlign: "center" }}>
              <Box
                component="span"
                sx={{
                  fontSize: "0.75rem",
                  fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                  color: "hsl(220, 10%, 46%)",
                }}
              >
                <FormattedMessage id="devise.already_subscribed" />{" "}
                <Link
                  onClick={() => onSectionClicked("login")}
                  underline="always"
                  sx={{
                    cursor: "pointer",
                    color: "hsl(153 68% 25%)",
                    textDecorationColor: "hsl(153 68% 25% / 0.4)",
                    "&:hover": {
                      textDecorationColor: "hsl(153 68% 25%)",
                    },
                  }}
                >
                  <FormattedMessage id="signin" />
                </Link>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default SignUp;
